import ReactDOM, { Root } from 'react-dom/client';
import moment from 'moment/moment';
import 'moment/min/locales.min.js';
import { createBrowserHistory } from 'history';
import React from 'react';
import axios from 'axios';
import { apiUrl, initContainer, InstanceContainer } from './config/binding/InstanceBinding';
import Keycloak, { KeycloakConfig } from 'keycloak-js';
import KeycloakStore from './config/KeycloakStore';
import GatewayContainer from './config/GatewayContainer';
import ServiceContainer from './config/ServiceContainer';
import HttpClient from './gateway/HttpClient';
import GatewayNames from './constant/GatewayNames';
import HttpMethod from './constant/HttpMethod';
import { KeycloakUserMethods, UserMethods } from './constant/MethodNames';
import { TenantModuleType, User, UserSystemAttributes } from './domain/rbac/RbacDomains';
import { store } from './redux/store';
import { setAttribute, setUser } from './redux/actions/UserActions';
import { changeLanguage } from './redux/actions/PageActions';
import { Provider } from 'react-redux';
import Loader from './routes/Loader';
import AppRouter from './routes/AppRouter';
import EnvironmentUtil from './util/EnvironmentUtil';
import Injectables from './constant/Injectables';
import { setTenantModule } from './redux/actions/ComponentActions';
import { UserClient } from './client/user/UserClient';
import { Socket } from 'socket.io-client';
import LanguageUtil from './util/LanguageUtil';
import DocumentLanguage from './util/DocumentLanguage';

class Bootstrap {
  private readonly root: Root;

  private environmentUtil: EnvironmentUtil | undefined;

  private lastActivity: number = Date.now();

  constructor() {
    this.root = ReactDOM.createRoot(document.getElementById('root') as HTMLElement);
  }

  public async init() {
    this.bindMomentLocale();
    this.bindJquery();
    try {
      const keycloakInstance = await this.prepareKeycloakInstance();
      if (keycloakInstance == undefined) return;
      const realm: string = history.location.pathname.split('/')[1];
      const auth = await keycloakInstance.init({
        onLoad: 'login-required',
      });
      if (!auth) {
        window.location.reload();
        return;
      } else {
        await this.checkTenant();
        localStorage.setItem('tenant-id', realm);
        this.initContainer(realm);
      }
      const httpClient: HttpClient = GatewayContainer.getInstance().get(GatewayNames.API_CLIENT);
      const login: {
        ip: string;
        module: TenantModuleType[];
      } = await httpClient.execute(HttpMethod.POST, UserMethods.LOGIN, undefined, undefined, { authorization: `Bearer ${keycloakInstance.token}` });
      const user = await httpClient.execute<User>(HttpMethod.GET, UserMethods.GET);
      const attributes = await httpClient.execute<UserSystemAttributes>(HttpMethod.GET, `${UserMethods.GET_ATTRIBUTE}/${user.id}`);
      const dispatch = store.dispatch;
      const language = localStorage.getItem('locale') || window.navigator.language || 'en-US';
      dispatch(setTenantModule(login.module));
      dispatch(setUser(user));
      dispatch(setAttribute(attributes));
      dispatch(changeLanguage(user.preferences.language));
      DocumentLanguage.apply(user.preferences.language);
      this.root.render(
        <Provider store={store}>
          <Loader>
            <AppRouter />
          </Loader>
        </Provider>,
      );
    } catch (e: any) {
      if (e.response?.data?.message == 'TENANT_NOT_EXISTS') {
        console.warn('Tenant not exists');
        this.root.render(
          <div className={'flex flex-col w-full h-screen items-center justify-center bg-[#F9FAFB] rounded-md'}>
            <img src={'/errors/not-found.svg'} alt='security' className={'w-[500px]'} />
            <p className={'text-[24px] text-[#4F4F4F] mt-[-40px]'}>Tenant Not Found</p>
            <p className={'text-[16px] text-[#4F4F4F] mt-[8px]'}>Tenant not exists in the system.</p>
          </div>,
        );
      }
      KeycloakStore.getKeycloakInstance()?.logout();
      console.log(e);
      console.error('Authenticated Failed');
    }
  }

  private bindMomentLocale() {
    // @ts-ignore
    const locale = window.navigator.userLanguage || window.navigator.language;
    moment.locale(locale);
  }

  private bindJquery() {
    // @ts-ignore
    if (window.$ == undefined || window.jquery == undefined || window.jQuery == undefined) {
      // @ts-ignore
      window.$ = window.jquery = window.jQuery = window.JQuery = require('jquery');
      // @ts-ignore
    }
  }

  private async getEnvironment(realm: string) {
    const response = await axios.get(`${apiUrl}/common/init`, {
      headers: {
        version: 'v1',
        'tenant-id': realm,
        authorization: 'Bearer test',
      },
    });
    const {
      url: { keycloak, transaction },
      feature: { ldapOnly, mouseTrack, pageChange, aiModel, tracker, expressionInputEnabled, watchlist, auditLogVisible, page, userActiveCheckMs, cra, ach },
      storage,
    } = response.data;
    this.environmentUtil = new EnvironmentUtil({
      keycloakUrl: keycloak,
      expressionInputEnabled,
      transactionUrl: transaction,
      ldapOnly,
      mouseTrack,
      pageChange,
      watchlist,
      auditLogVisible,
      aiModel,
      tracker,
      page,
      userActiveCheckMs,
      cra,
      ach: ach,
      storageMaxSize: storage?.maxSize,
    });
    return this.environmentUtil.getKeycloakUrl();
  }

  private async prepareKeycloakInstance(): Promise<Keycloak | undefined> {
    const realm: string = history.location.pathname.split('/')[1];
    const clientId: string = `${realm}-riskoffice-client`;
    if (realm == '') {
      this.root.render(
        <div className={'flex flex-col w-full h-screen items-center justify-center bg-[#F9FAFB] rounded-md'}>
          <img src={'/errors/not-found.svg'} alt='security' className={'w-[500px]'} />
          <p className={'text-[24px] text-[#4F4F4F] mt-[-40px]'}>Select a Realm</p>
          <p className={'text-[16px] text-[#4F4F4F]'}>Realm name is required in URL.</p>
        </div>,
      );
      return;
    }
    let keycloakUrl;
    keycloakUrl = await this.getEnvironment(realm);
    const initOptions: KeycloakConfig = {
      url: `${keycloakUrl}`,
      realm,
      clientId,
    };
    KeycloakStore.init(initOptions);
    return KeycloakStore.getKeycloakInstance();
  }

  private initContainer(realm: string) {
    if (this.environmentUtil) {
      InstanceContainer.bind(Injectables.ENVIRONMENT_UTIL).toConstantValue(this.environmentUtil);
    }
    initContainer(realm);
    GatewayContainer.initialize(realm);
    ServiceContainer.initialize(realm);

    const userClient: UserClient = GatewayContainer.getInstance().get(GatewayNames.USER_CLIENT);
    const socket: Socket = InstanceContainer.get(Injectables.SOCKET_CLIENT);
    setInterval(() => {
      KeycloakStore.getKeycloakInstance()
        ?.updateToken(60)
        .then(async (refreshed) => {
          try {
            if (!refreshed) return;
            await userClient.updateToken(KeycloakStore.getKeycloakInstance()?.token!);
            socket.io.opts.extraHeaders!.authorization = `Bearer ${KeycloakStore.getKeycloakInstance()?.token}`;
          } catch (e) {
            console.error('Token refresh failed', e);
          }
        });
    }, 10_000);
    const userActiveCheckMs = this.environmentUtil?.getUserActiveCheckMs();
    if (userActiveCheckMs && userActiveCheckMs > 0) {
      this.startCheckActivity(userActiveCheckMs);
    }
  }

  private async checkTenant() {
    const realm: string = history.location.pathname.split('/')[1];
    const previous = localStorage.getItem('tenant-id');
    if (previous != undefined && previous != realm) {
      localStorage.removeItem('state');
      localStorage.removeItem('react-refresh-token');
      localStorage.removeItem('sub');
      localStorage.removeItem('tenant-id');
      const httpClient: HttpClient = GatewayContainer.getInstance(previous).get(GatewayNames.API_CLIENT);
      await httpClient.execute(HttpMethod.POST, KeycloakUserMethods.LOGOUT);
      GatewayContainer.clear();
      //await KeycloakStore.getKeycloakInstance()?.logout();
    }
  }

  private startCheckActivity(checkMs: number) {
    document.addEventListener('mousemove', () => {
      this.lastActivity = Date.now();
    });
    setInterval(() => {
      const now = Date.now();
      if (now - this.lastActivity > checkMs) {
        KeycloakStore.getKeycloakInstance()?.logout();
      }
    }, 60_000);
  }
}

export const history = createBrowserHistory();

export default Bootstrap;
